'use client';

import MiniSearch from 'minisearch';
import { FILTER_MAP } from './search-categories';

export interface SearchDocument {
  id: string;
  title: string;
  description: string;
  keywords: string;
  href: string;
  pageType: string;
  icon: string;
  countryCode: string;
  countryFlag: string;
  sublabel: string;
}

export interface SearchResult extends SearchDocument {
  score: number;
}

// Same options used at build time — must match exactly
const MINISEARCH_OPTIONS = {
  fields: ['title', 'description', 'keywords'],
  storeFields: ['title', 'description', 'keywords', 'href', 'pageType', 'icon', 'countryCode', 'countryFlag', 'sublabel'],
};

const SEARCH_OPTIONS = {
  boost: { title: 3, keywords: 2, description: 1 },
  fuzzy: 0.2,
  prefix: true,
};

// Post-search boost by page type (applied as multiplier to MiniSearch scores)
export const PAGE_TYPE_BOOST: Record<string, number> = {
  country: 10,
  city: 8,
  university: 7,
  admission: 6,
  'university-admission': 6,
  phenomenon: 6,
  'pathway-comparison': 5,
  'admission-calendar': 5,
  'scholarship-screener': 5,
  'admission-faq': 5,
  'best-time': 5,
  'long-weekend': 5,
  'month-hub': 4,
  'type-hub': 4,
  'religion-hub': 4,
  'nature-hub': 4,
  'university-hub': 4,
  'country-month': 4,
  'festival-index': 4,
  'cross-calendar': 3,
  'country-religion': 3,
  'country-nature': 3,
  'event-detail': 3,
};

type ShardKey = 'nav' | 'events';

class AlmanakSearch {
  private shards: Partial<Record<ShardKey, MiniSearch<SearchDocument>>> = {};
  private loading: Partial<Record<ShardKey, Promise<void>>> = {};
  private cacheVersion: string | null = null;
  private metaLoading: Promise<void> | null = null;

  /** Fetch meta.json once to get builtAt for cache busting */
  private async ensureCacheVersion(): Promise<string> {
    if (this.cacheVersion) return this.cacheVersion;
    if (!this.metaLoading) {
      this.metaLoading = fetch('/search/meta.json')
        .then(res => res.ok ? res.json() : {})
        .then((meta: { builtAt?: string }) => {
          this.cacheVersion = meta.builtAt || '';
        })
        .catch(() => { this.cacheVersion = ''; });
    }
    await this.metaLoading;
    return this.cacheVersion || '';
  }

  /** Load a shard from the pre-built JSON file */
  async loadShard(key: ShardKey): Promise<void> {
    if (this.shards[key]) return;
    if (this.loading[key]) return this.loading[key];

    this.loading[key] = (async () => {
      try {
        const v = await this.ensureCacheVersion();
        const suffix = v ? `?v=${encodeURIComponent(v)}` : '';
        const res = await fetch(`/search/shard-${key}.json${suffix}`);
        if (!res.ok) throw new Error(`Failed to load search shard: ${key}`);
        const text = await res.text();
        this.shards[key] = MiniSearch.loadJSON<SearchDocument>(text, MINISEARCH_OPTIONS);
        delete this.loading[key];
      } catch (err) {
        console.error(`[AlmanakSearch] Failed to load shard ${key}:`, err);
        delete this.loading[key];
        throw err;
      }
    })();

    return this.loading[key];
  }

  /** Check if a shard is loaded */
  isLoaded(key: ShardKey): boolean {
    return !!this.shards[key];
  }

  /** Search across loaded shards */
  search(query: string, options?: { filter?: string; limit?: number }): SearchResult[] {
    const { filter, limit = 20 } = options || {};
    const results: SearchResult[] = [];

    for (const [, shard] of Object.entries(this.shards)) {
      if (!shard) continue;
      const raw = shard.search(query, SEARCH_OPTIONS) as unknown as Array<{ score: number } & SearchDocument>;
      for (const hit of raw) {
        // Apply page type boost
        const boost = PAGE_TYPE_BOOST[hit.pageType] || 1;
        results.push({ ...hit, score: hit.score * boost });
      }
    }

    // Apply category filter
    let filtered = results;
    if (filter && filter !== 'all') {
      filtered = results.filter(r => categoryMatchesFilter(r.pageType, filter));
    }

    // Sort by boosted score descending
    filtered.sort((a, b) => b.score - a.score);

    return filtered.slice(0, limit);
  }
}

/** Maps a pageType to whether it belongs in a given filter tab */
export function categoryMatchesFilter(pageType: string, filter: string): boolean {
  return (FILTER_MAP as Record<string, string[]>)[filter]?.includes(pageType) ?? false;
}

// Singleton
export const almanakSearch = new AlmanakSearch();
